export class Linq {

  static groupBy<TItem, TKey>(
    collection: TItem[],
    keySelector: (item: TItem) => TKey): Map<TKey, TItem[]> {

    const callback = (previous: Map<TKey, TItem[]>, currentItem: TItem) => {
      const group = keySelector(currentItem);
      if (!previous.has(group)) {
        previous.set(group, []);
      }
      previous.get(group).push(currentItem);
      return previous;
    };
    const initialValue = new Map<TKey, TItem[]>();
    const result = collection.reduce(callback, initialValue);
    return result;
  }

  static orderBy<TItem, TKey>(
    collection: TItem[],
    keySelector: (item: TItem) => TKey,
    isKey1GreaterThanKey2?: (key1: TKey, key2: TKey) => boolean,
    sortInPlace = false): TItem[] {

    if (!collection) {
      throw new Error(`'collection' argument is null or undefined.`);
    }
    if (!keySelector) {
      throw new Error(`'keySelector' argument is null or undefined.`);
    }
    isKey1GreaterThanKey2 ??= (key1: TKey, key2: TKey) => key1 > key2;
    collection = sortInPlace ? collection : [...collection];

    const result = collection.sort((item1, item2) => {
      const key1 = keySelector(item1);
      const key2 = keySelector(item2);
      return isKey1GreaterThanKey2(key1, key2) ? 1 : isKey1GreaterThanKey2(key2, key1) ? -1 : 0;
    });
    return result;
  }

  static maxBy<TItem, TKey>(
    collection: TItem[],
    keySelector: (item: TItem) => TKey,
    isKey1GreaterThanKey2?: (key1: TKey, key2: TKey) => boolean): TItem {

    if (!collection) {
      throw new Error(`'collection' argument is null or undefined.`);
    }
    if (!keySelector) {
      throw new Error(`'keySelector' argument is null or undefined.`);
    }
    if (collection.length === 0) {
      throw new Error(`'collection' argument is empty.`);
    }
    isKey1GreaterThanKey2 ??= (key1: TKey, key2: TKey) => key1 > key2;

    const accumulator = (maxItem: TItem, currentItem: TItem) => {
      const [maxKey, currentKey] = [keySelector(maxItem), keySelector(currentItem)];
      return isKey1GreaterThanKey2(currentKey, maxKey) ? currentItem : maxItem;
    };
    const seed = collection[0];
    const result = collection.reduce(accumulator, seed);
    return result;
  }

  static distinct<TItem, TKey>(
    collection: TItem[],
    keySelector?: (item: TItem) => TKey): TItem[] {

    if (!collection) {
      throw new Error(`'collection' argument is null or undefined.`);
    }
    keySelector ??= (item: TItem) => item as unknown as TKey;

    const distinctItems: TItem[] = [];
    const seenKeys: Set<TKey> = new Set();
    for (const item of collection) {
      const key = keySelector(item);
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        distinctItems.push(item);
      }
    }
    return distinctItems;
  }
}
